/**
 * Terminaenderungen (DSV) - unbestaetigte Aenderungen an Sendungsterminen (ETA/ETD, weitere
 * Terminarten folgen), geclustert je Sendung. Schnittstelle von Auftrag E (Backend, paralleler
 * Zweig): `GET /api/dsv/terminaenderungen` liefert ausschliesslich UNBESTAETIGTE Aenderungen,
 * `POST /api/dsv/terminaenderungen/bestaetigen` mit `{ ids }` bestaetigt eine Menge davon.
 *
 * Beide laufen ueber `anfrage()` (`./basis.ts`) - derselbe authentifizierte Weg wie jede andere
 * API-Anfrage im Projekt (`Authorization: Bearer`, siehe dessen Kopfkommentar). Ohne ihn liefe jeder
 * Aufruf in einen 401, sichtbar nur als leeres Panel - genau der Fehler, der an anderer Stelle im
 * Projekt schon einmal ein ganzes Feature unbemerkt hat scheitern lassen.
 */
import { anfrage } from "./basis.ts";

/** Eine mit der Sendung verknuepfte Bestellung - der Absprungpunkt in `Terminaenderungen.tsx`. */
export interface TerminaenderungBestellung {
  orderId: string;
  bestellnummer: string;
  /**
   * Die volle JTL-Bestellnummer (`cEigeneBestellnummer`, z. B. "BE-10281"), Auftrag
   * "be-*-Bestellnummer statt jtl_order_id in Terminaenderungen-Pills" (01.09.2026). `null`, wenn
   * das Backend sie nicht ermitteln konnte (kein JTL-Zugang konfiguriert, JTL nicht erreichbar,
   * kein Treffer) - `Terminaenderungen.tsx` faellt dann auf `bestellnummer` zurueck, dasselbe
   * Fallback-Muster wie `lib/bestellkennung.ts#kennungVon`.
   */
  orderNo: string | null;
  /**
   * `tlieferant.cFirma` (Auftrag "Übersichtsseite - Untertitel, Bestellbezug, Lieferant bei
   * Terminänderungen, Breiten-Check", 06.09.2026) - derselbe Rundlauf/dieselben Rückfallgründe wie
   * `orderNo` (`backend/src/domaene/terminaenderungenListe.ts#BestellungsBezugAntwort`). `null`, wenn
   * das Backend ihn nicht ermitteln konnte - `Terminaenderungen.tsx` zeigt dann schlicht nichts an,
   * kein Platzhaltertext.
   */
  lieferant: string | null;
}

/**
 * Eine einzelne Terminaenderung. `feld` ist im Backend bewusst freier Text (weitere Terminarten
 * folgen) - `altWert`/`neuWert` sind `null`, wenn der Termin vorher unbekannt war bzw. jetzt entfaellt
 * (siehe `Terminaenderungen.tsx`, Darstellung).
 */
export interface Terminaenderung {
  id: number;
  feld: string;
  altWert: string | null;
  neuWert: string | null;
  quelle: string;
  erkanntAm: string;
  bestaetigtAm: string | null;
  bestaetigtVon: string | null;
}

/**
 * Wie eine `TerminZeile` zu lesen ist (Plan 2026-10-18, Verschiebungsbalken; Spiegel von
 * `backend/src/domaene/terminZeilen.ts#TerminArt`): `verschiebung` = ein bekannter Termin wurde
 * verschoben (nur hier gibt es einen Balken), `erstbefuellung` = der Termin war bisher unbekannt,
 * `zugeordnet` = durch eine neue Verknuepfung hinzugekommen, `sonstig` = alles Uebrige.
 */
export type TerminArt = "verschiebung" | "erstbefuellung" | "zugeordnet" | "sonstig";

/**
 * Eine Zeile je Sendung und Terminart (`feld`), zusammengefasst aus allen Rohzeilen der Sendung
 * (Spiegel von `backend/src/domaene/terminZeilen.ts#TerminZeile`). Datumswerte sind "YYYY-MM-DD".
 * `ursprung` = erster bekannter Termin (nur bei `verschiebung`), `bekannt` = Stand bei der letzten
 * Kenntnisnahme, `aktuell` = heutiger Termin; `null`, wo die Art den Wert nicht kennt oder der Termin
 * entfallen ist. `erkanntAm` ist der ISO-Zeitstempel der spaetesten offenen Zeile dieses Felds.
 * Die Rechnung daraus (Tage, Stufe, Balken) steht in `lib/terminVerschiebung.ts`.
 */
export interface TerminZeile {
  feld: string;
  art: TerminArt;
  ursprung: string | null;
  bekannt: string | null;
  aktuell: string | null;
  erkanntAm: string;
}

/** Eine Sendung mit ihren (unbestaetigten) Terminaenderungen und den zugehoerigen Bestellungen. */
export interface SendungMitTerminaenderungen {
  shipmentId: string;
  dsvShipmentNo: string;
  bestellungen: TerminaenderungBestellung[];
  /** Rohzeilen, eine je Bestellung und Aenderung - ihre ids gehen an "Zur Kenntnis genommen". */
  aenderungen: Terminaenderung[];
  /** Eine zusammengefasste Zeile je Terminart - das, was das Panel anzeigt. */
  termine: TerminZeile[];
}

/** Antwort von `GET /api/dsv/terminaenderungen`. */
export interface TerminaenderungenAntwort {
  sendungen: SendungMitTerminaenderungen[];
  offenGesamt: number;
}

/** Antwort von `POST /api/dsv/terminaenderungen/bestaetigen`. */
export interface TerminaenderungenBestaetigenAntwort {
  bestaetigt: number;
  uebersprungen: number;
}

/** `GET /api/dsv/terminaenderungen` - nur unbestaetigte Aenderungen, siehe Kopfkommentar. */
export function terminaenderungenLaden(signal?: AbortSignal): Promise<TerminaenderungenAntwort> {
  return anfrage<TerminaenderungenAntwort>("/api/dsv/terminaenderungen", { signal });
}

/** `POST /api/dsv/terminaenderungen/bestaetigen` mit `{ ids }`. */
export function terminaenderungenBestaetigen(
  ids: readonly number[],
): Promise<TerminaenderungenBestaetigenAntwort> {
  return anfrage<TerminaenderungenBestaetigenAntwort>("/api/dsv/terminaenderungen/bestaetigen", {
    methode: "POST",
    koerper: { ids },
  });
}
